/**
 * Admin: per-property bulk pickup policy editor + incoming requests list with photos.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Boxes, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQ_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'on_request', label: 'On request' },
];

const DAY_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_POLICY = {
  enabled: false,
  frequency: 'monthly',
  pickup_day_of_month: 1,
  day_of_week: 5,
  time_window: '8 AM - 12 PM',
  accepted_items: 'Furniture, mattresses, small appliances, large boxes',
  not_accepted_items: 'Hazardous materials, paint, electronics with screens, construction debris, tires',
  max_items: 5,
  additional_fee: 0,
  notice_required_hours: 48,
  instructions: '',
};

export default function AdminBulkPickup() {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [requests, setRequests] = useState([]);
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, request: null, date: '' });

  const loadProps = async () => {
    try {
      const res = await fetch(`${API}/api/properties`, { credentials: 'include' });
      if (res.ok) {
        const list = await res.json();
        setProperties(list);
        if (!propertyId && list.length) setPropertyId(list[0].property_id);
      }
    } catch (_) { /* noop */ }
  };

  const loadPolicy = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${API}/api/ops/properties/${propertyId}/bulk-policy`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const existing = data.bulk_collection || {};
        setPolicy({ ...DEFAULT_POLICY, ...existing, enabled: !!existing.enabled });
      }
    } catch (_) { /* noop */ }
  };

  const loadRequests = async () => {
    try {
      const res = await fetch(`${API}/api/ops/bulk-pickup`, { credentials: 'include' });
      if (res.ok) setRequests(await res.json());
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadProps(); loadRequests(); }, []);
  useEffect(() => { loadPolicy(); }, [propertyId]);

  const save = async () => {
    if (!propertyId) return toast.error('Select a property');
    try {
      const res = await fetch(`${API}/api/ops/properties/${propertyId}/bulk-policy`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(policy),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Save failed');
      else toast.success(policy.enabled ? 'Bulk removal enabled — tenants notified.' : 'Policy saved.');
    } catch (_) {
      toast.error('Network error');
    }
  };

  const setStatus = async (requestId, status, scheduled_for = null) => {
    try {
      const res = await fetch(`${API}/api/ops/bulk-pickup/${requestId}/status`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, scheduled_for }),
      });
      if (res.ok) {
        toast.success(`Marked ${status}`);
        loadRequests();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed');
      }
    } catch (_) { /* noop */ }
  };

  const openSchedule = (req) => setScheduleDialog({ open: true, request: req, date: '' });

  const confirmSchedule = async () => {
    if (!scheduleDialog.date) return toast.error('Pick a date');
    await setStatus(scheduleDialog.request.request_id, 'scheduled', scheduleDialog.date);
    setScheduleDialog({ open: false, request: null, date: '' });
  };

  const statusColor = (s) => ({
    submitted: 'bg-zinc-600',
    scheduled: 'bg-amber-600 text-black',
    completed: 'bg-emerald-600',
    cancelled: 'bg-red-700',
  }[s] || 'bg-zinc-600');

  return (
    <div className="space-y-4">
      {/* Policy editor */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6" data-testid="admin-bulk-policy-card">
        <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Boxes className="h-6 w-6 text-[#FFD60A]" /> BULK REMOVAL — PER-PROPERTY POLICY
        </h2>
        <p className="text-sm text-zinc-400 mb-4">
          Toggle ON only after the bulk-collection contract is signed. When enabled, the property's residents see a "Request Bulk Removal" card in their app and get notified instantly.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-zinc-300 mb-1 block">Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="admin-bulk-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch
              checked={policy.enabled}
              onCheckedChange={(v) => setPolicy({ ...policy, enabled: v })}
              data-testid="admin-bulk-enable-switch"
            />
            <span className="text-white font-semibold">{policy.enabled ? 'Enabled — visible to residents' : 'Disabled — hidden'}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-zinc-300">Frequency</Label>
            <Select value={policy.frequency} onValueChange={(v) => setPolicy({ ...policy, frequency: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="admin-bulk-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {policy.frequency === 'monthly' ? (
            <div>
              <Label className="text-zinc-300">Day of month (1-28)</Label>
              <Input
                type="number" min={1} max={28}
                value={policy.pickup_day_of_month || 1}
                onChange={(e) => setPolicy({ ...policy, pickup_day_of_month: parseInt(e.target.value) || 1 })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          ) : policy.frequency !== 'on_request' ? (
            <div>
              <Label className="text-zinc-300">Day of week</Label>
              <Select value={String(policy.day_of_week ?? 5)} onValueChange={(v) => setPolicy({ ...policy, day_of_week: parseInt(v) })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAY_OF_WEEK.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : <div />}
          <div>
            <Label className="text-zinc-300">Time window</Label>
            <Input
              value={policy.time_window || ''}
              onChange={(e) => setPolicy({ ...policy, time_window: e.target.value })}
              placeholder="e.g., 8 AM - 12 PM"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-zinc-300">Notice required (hours)</Label>
            <Input
              type="number" min={0} value={policy.notice_required_hours || 48}
              onChange={(e) => setPolicy({ ...policy, notice_required_hours: parseInt(e.target.value) || 0 })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Max items / pickup (0 = unlimited)</Label>
            <Input
              type="number" min={0} value={policy.max_items || 0}
              onChange={(e) => setPolicy({ ...policy, max_items: parseInt(e.target.value) || 0 })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Additional fee ($)</Label>
            <Input
              type="number" step="0.01" min={0} value={policy.additional_fee || 0}
              onChange={(e) => setPolicy({ ...policy, additional_fee: parseFloat(e.target.value) || 0 })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-zinc-300">Accepted items</Label>
            <Textarea rows={2}
              value={policy.accepted_items || ''}
              onChange={(e) => setPolicy({ ...policy, accepted_items: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          <div>
            <Label className="text-zinc-300">Items NOT accepted</Label>
            <Textarea rows={2}
              value={policy.not_accepted_items || ''}
              onChange={(e) => setPolicy({ ...policy, not_accepted_items: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
        </div>

        <div className="mb-3">
          <Label className="text-zinc-300">Tenant-facing instructions</Label>
          <Textarea rows={2}
            value={policy.instructions || ''}
            onChange={(e) => setPolicy({ ...policy, instructions: e.target.value })}
            placeholder="e.g., 'Place items by the side gate the night before pickup.'"
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
        </div>

        <Button
          onClick={save}
          className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
          data-testid="admin-bulk-save-btn"
        >
          <Save className="h-4 w-4 mr-2" /> Save & Notify Residents
        </Button>
      </Card>

      {/* Incoming requests */}
      <Card className="bg-zinc-900 border-zinc-700 p-6" data-testid="admin-bulk-requests-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#FFD60A]" /> Incoming bulk-removal requests
          </h3>
          <Badge className="bg-[#FFD60A] text-black">{requests.length}</Badge>
        </div>
        {requests.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">No bulk-removal requests yet.</p>
        ) : (
          <ul className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {requests.map((r) => (
              <li
                key={r.request_id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4"
                data-testid={`admin-bulk-row-${r.request_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-bold text-white">
                      {r.property_name} · Unit {r.unit_number}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {r.tenant_name} · {new Date(r.created_at).toLocaleString()}
                      {r.requested_for_date && <span className="text-amber-400"> · prefers {r.requested_for_date}</span>}
                    </div>
                    <p className="text-sm text-zinc-200 mt-2">
                      <span className="font-semibold">{r.item_count} item(s):</span> {r.items_description}
                    </p>
                    {r.notes && <p className="text-xs text-zinc-400 mt-1 italic">"{r.notes}"</p>}
                    {r.scheduled_for && <p className="text-xs text-amber-400 mt-1">Scheduled for {r.scheduled_for}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColor(r.status)}>{r.status}</Badge>
                  </div>
                </div>

                {Array.isArray(r.photos) && r.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto">
                    {r.photos.slice(0, 6).map((p, i) => (
                      <img
                        key={i}
                        src={p.startsWith('data:') ? p : `data:image/jpeg;base64,${p}`}
                        alt={`bulk-${i}`}
                        className="h-20 w-20 object-cover rounded border border-zinc-600"
                      />
                    ))}
                  </div>
                )}

                {r.status === 'submitted' && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-600" onClick={() => openSchedule(r)} data-testid={`admin-bulk-schedule-${r.request_id}`}>
                      Schedule Pickup
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10" onClick={() => setStatus(r.request_id, 'cancelled')} data-testid={`admin-bulk-cancel-${r.request_id}`}>
                      Cancel
                    </Button>
                  </div>
                )}
                {r.status === 'scheduled' && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setStatus(r.request_id, 'completed')} data-testid={`admin-bulk-complete-${r.request_id}`}>
                      Mark Completed
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Schedule dialog */}
      <Dialog open={scheduleDialog.open} onOpenChange={(v) => !v && setScheduleDialog({ ...scheduleDialog, open: false })}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#3D3D3D]">Schedule Bulk Removal</DialogTitle>
          </DialogHeader>
          {scheduleDialog.request && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-700">
                {scheduleDialog.request.property_name} · Unit {scheduleDialog.request.unit_number}<br/>
                {scheduleDialog.request.item_count} item(s): {scheduleDialog.request.items_description}
              </p>
              <div>
                <Label htmlFor="bulk-sched-date">Pickup date *</Label>
                <Input
                  id="bulk-sched-date"
                  type="date"
                  value={scheduleDialog.date}
                  onChange={(e) => setScheduleDialog({ ...scheduleDialog, date: e.target.value })}
                  data-testid="admin-bulk-schedule-date-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog({ open: false, request: null, date: '' })}>Cancel</Button>
            <Button
              onClick={confirmSchedule}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
              data-testid="admin-bulk-schedule-confirm-btn"
            >
              Confirm & Notify Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
